/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-console */
import { FocusEvent, useContext } from 'react'
import { TmsContext } from '@awaze/mfe-common.contexts.tms'
import { Document } from '@contentful/rich-text-types'
import { GlobalProps } from 'src/utils/globalProps'
import { NewsletterSignup } from '@awaze/design-system.compositions.newsletter-signup'
import { SectionModule } from '@/components/section-composition/section'
import RichText from '../RichText/RichText'
import { Asset } from 'contentful'

export interface NewsletterSignUpProps {
  image?: Asset
  title?: string
  text?: Document
  firstName?: string
  lastName?: string
  email?: string
  ctaButton?: string
  termsConditionsPrivacy?: Document
  submittedFormTitle?: string
  submittedFormText?: string
  errorMessage?: string
  requiredFirstnameText?: string
  requiredLastnameText?: string
  requiredEmailText?: string
  invalidEmailText?: string
  formId?: string
  active?: boolean
  locale?: string
  reCaptchaClientKey?: string
}

export const NewsletterFormDataLayerId = 'newsletter-signup-form'

export const NewsletterSignUp: React.FC<NewsletterSignUpProps> = ({
  image,
  title,
  text,
  firstName,
  lastName,
  email,
  ctaButton,
  termsConditionsPrivacy,
  submittedFormTitle,
  submittedFormText,
  errorMessage,
  active = true,
  locale = 'en-US',
  reCaptchaClientKey,
  requiredFirstnameText,
  requiredLastnameText,
  requiredEmailText,
  invalidEmailText,
}) => {
  const { brand } = useContext(GlobalProps)
  const { file: { url: src = '' } = {}, description: alt = '' } =
    image?.fields || {}
  const tms = useContext(TmsContext)

  const onFormSubmitSuccess = (emailaddress) => {
    tms?.updateDataLayer('link', 'form-complete', {
      form_id: NewsletterFormDataLayerId,
      user_email: emailaddress,
    })
  }

  const onFormSubmitError = (error: Error) => {
    tms?.updateDataLayer('link', 'form-fail', {
      form_id: NewsletterFormDataLayerId,
      form_fail_reason: `An error occurred: ${error.message}`,
    })
  }

  const onFieldFocus = (event: FocusEvent<HTMLFormElement, Element>) => {
    if (event.target.nodeName === 'INPUT') {
      const fieldName = event.target.getAttribute('data-testid')
      tms?.updateDataLayer('link', 'form-input', {
        form_id: NewsletterFormDataLayerId,
        form_field_name: event.target.getAttribute('name'),
        form_field_position:
          fieldName === 'firstname' ? 1 : fieldName === 'lastname' ? 2 : 3,
      })
    }
    if (
      event.target.nodeName === 'BUTTON' &&
      event.target.getAttribute('data-testid') === 'signup'
    ) {
      tms?.updateDataLayer('link', 'form-submit', {
        form_id: NewsletterFormDataLayerId,
      })
    }
  }

  return active ? (
    <SectionModule section={{ backgroundColor: 'white' }}>
      <NewsletterSignup
        {...{
          brand,
          image: {
            src: `${src}?w=800&fm=webp&q=80`,
            alt: alt,
          },
          title,
          firstNamePlaceholder: firstName,
          lastNamePlaceholder: lastName,
          emailPlaceholder: email,
          ctaButton,
          submittedFormTitle,
          submittedFormText,
          locale,
          reCaptchaClientKey,
        }}
        text={text && <RichText content={text} />}
        termsConditionsPrivacy={
          termsConditionsPrivacy && (
            <RichText content={termsConditionsPrivacy} />
          )
        }
        errors={{
          errorMessage,
          requiredFirstnameText,
          requiredLastnameText,
          requiredEmailText,
          invalidEmailText,
        }}
        onFormSubmitSuccess={onFormSubmitSuccess}
        onFormSubmitError={onFormSubmitError}
        onFieldFocus={onFieldFocus}
      />
    </SectionModule>
  ) : null
}
